import copy from 'copy-to-clipboard'
import { Eye, EyeOff, Rocket } from 'lucide-vue-next'
import { defineStore, storeToRefs } from 'pinia'
import { inject, nextTick, shallowRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotificationStore } from './notificationStore'
import { useFullscreenStore } from './fullscreenStore'
import { useSearchStore } from './searchStoreV2'
import { useImageStore } from './imageStore'
import { useGeneratorStore } from './GeneratorStore'
import { useAuthStore } from './authStore'
import { useDynamicModalStore } from './dynamicModalStore'
import type { GenerationInfo, IImage, Modifier } from '@/types'
import { ENotificationType, ImageCreativity, ImageResolution } from '@/types'
import { i18n } from '@/i18n'
import type { RequestRouter } from '@/request/requestRouter'
import { RequestAction } from '@/request/requestRouter'
import FapFapError from '@/errors/FapFapError'
import { useGraphicStyles } from '@/composables/useGraphicStyles'

// @ts-ignore
const { t } = i18n.global

export const useImageActionStore = defineStore('imageActionStore', () => {
	const downloadState = shallowRef<boolean>(false)
	const canShare = shallowRef<boolean>(!!navigator.share)
	const router = useRouter()
	const route = useRoute()
	const request = inject<RequestRouter>('request')

	async function downloadImage(uuid: string, isUpscaled: boolean) {
		if (uuid) {
			downloadState.value = true
			try {
				const response = await fetch(`${import.meta.env.VITE_CLOUDFLARE_URL}/${uuid}${isUpscaled ? '-upscaled' : ''}.png`)
				const blob = await response.blob()
				const objectUrl = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = objectUrl
				link.download = uuid ?? 'image.png'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(objectUrl)
			}
			catch (error) {
				console.error(error)
			}
		}
		else {
			console.error(`Image source is not defined`)
		}
		downloadState.value = false
	}

	async function copyOrShare(uuid: string) {
		if (navigator.share) {
			try {
				await navigator.share({
					title: t('share.title'),
					text: t('share.text'),
					url: `${import.meta.env.VITE_HOST}/image/${uuid}`,
				})
			}
			catch (error) {
				console.error('Sharing error:', error)
			}
		}
		else if (uuid) {
			copy(`${import.meta.env.VITE_HOST}/image/${uuid}`)
			useNotificationStore().push(ENotificationType.App, {
				key: `notification.clipboard`,
				delay: 4000,
			})
		}
	}

	async function seeSimilar(uuid: string): Promise<void> {
		const searchStore = useSearchStore()
		const image = useImageStore().getImage(uuid) as IImage
		const { prompts } = storeToRefs(searchStore)

		if (useFullscreenStore().isFullscreen) {
			router.back()
		}

		const tags = await request?.exec(RequestAction.GetImageTagsId, {
			routeParams: {
				uuid,
			},
		})
		if (tags) {
			prompts.value.pos = []
			prompts.value.neg = []
			prompts.value.opt = image.tags.split(',').map((tag: string, index: number) => {
				return {
					id: tags[index],
					value: tag,
				}
			})
			await nextTick()
			searchStore.search()
			searchStore.openPanel = true
		}
	}

	async function report(uuid: string): Promise<void> {
		const res = await request?.exec(RequestAction.ReportImage, {
			body: {
				uuid,
			},
			routeParams: {
				flag: 0,
			},
		})
		if (res && res.ok) {
			useNotificationStore().push(ENotificationType.App, {
				key: `notification.report`,
				delay: 4000,
			})
		}
		else {
			console.error(`Failed to report image with uuid: ${uuid}`)
		}
	}

	async function togglePrivacy(uuid: string, emit: (isPrivate: boolean) => void) {
		const res = await request?.exec(RequestAction.ToggleImagePrivacy, {
			routeParams: {
				uuid,
			},
		})
		if (res) {
			const isPrivate = await res?.json()
			useImageStore().updateAllTargetImage(uuid, {
				is_private: isPrivate,
			})
			emit(isPrivate)
			useNotificationStore().push(ENotificationType.App, {
				key: isPrivate ? 'notification.image_private' : 'notification.image_public',
				icon: isPrivate ? EyeOff : Eye,
				iconColor: 'text-orange-400',
				delay: 4000,
			}, true)
		}
	}

	async function clone(uuid: string, fromRecreate: boolean = false): Promise< void> {
		let image: any = useImageStore().getImage(uuid)
		if (!image) {
			image = useFullscreenStore().directLinkImage
		}
		if (!image) {
			image = {}
		}

		if (!image.gen_data || Object.keys(image.gen_data).length <= 2) {
			const res = await request?.exec(RequestAction.GetImageGenInfo, {
				routeParams: {
					uuid,
				},
			})
			if (!(res instanceof FapFapError)) {
				image.gen_data = res
			}
		}
		if (image.gen_data) {
			const genStore = useGeneratorStore()
			const { generatorPrompt } = storeToRefs(genStore)

			const style = useGraphicStyles().getStyle(image.style_id)

			const propToClone: Array<keyof GenerationInfo> = [
				'creativity',
				'quality',
				'neg_prompt',
				'prompt',
				'seed',
				'format',
				'watermark',
				'style_id',
				'modifiers',
				'less_nsfw',
			]

			generatorPrompt.value.modifiers = []
			await nextTick()
			propToClone.forEach((p) => {
				if (image?.gen_data) {
					if (p === 'modifiers' && image.gen_data[p]) {
						(generatorPrompt.value as any)[p] = image.gen_data[p].map((mod: any) => {
							const newMod = genStore.getModifier(mod.id)
							if (newMod) {
								newMod.weight = mod.weight
							}
							return newMod
						}).filter(Boolean)
					}
					else {
						if (p === 'prompt') {
							generatorPrompt.value.prompt = image.gen_data[p] ?? ''
						}
						else if (p === 'quality' && !useAuthStore().isPremium) {
							generatorPrompt.value[p] = ImageResolution.Standard
						}
						else if (p !== 'modifiers') {
							(generatorPrompt.value as any)[p] = image.gen_data[p]
						}
					}
				}
			})
			if (!generatorPrompt.value?.creativity) {
				generatorPrompt.value.creativity = ImageCreativity.Balanced
			}
			if (fromRecreate) {
				generatorPrompt.value.seed++
			}

			genStore.selectedStyle = style

			if (route.name !== 'Create') {
				router.push('/create')
			}
		}
	}

	return {
		downloadImage,
		downloadState,
		copyOrShare,
		canShare,
		seeSimilar,
		report,
		togglePrivacy,
		clone,
	}
})
